import { useState } from "react";
import type { AssetDetail, Dressing } from "../api/types";
import { Button } from "../ui";
import {
  type SlotDoc,
  type SlotSurface,
  humanise,
  slotSurface,
} from "../viewer/math";
import { ArtworkPicker } from "./ArtworkPicker";
import { ColourControl } from "./ColourControl";
import { SurfacePicker } from "./SurfacePicker";
import { useDressingDetails } from "./useDressingDetails";

/** The classes for which a surface of the 3D library can be chosen; a
 *  surface that only keeps its own texture, or only takes print, has none. */
const CHOOSABLE = ["hard", "soft", "glass", "screen"];

function without(map: Record<string, string>, slot: string) {
  const next = { ...map };
  delete next[slot];
  return next;
}

function withoutSkin(map: Dressing["skins"], slot: string) {
  const next = { ...map };
  delete next[slot];
  return next;
}

function Swatch({ hex }: { hex: string }) {
  return (
    <span
      aria-hidden="true"
      data-testid="swatch"
      title={hex}
      className="inline-block h-4 w-4 shrink-0 rounded-full border border-slate-300"
      style={{ background: hex }}
    />
  );
}

/** "Surfaces": one row per surface of an object, saying what is on it. A row
 *  is a button that selects its surface (the keyboard's way to what a click
 *  on the 3D view does); under the selected row are the ways to change it.
 *  `dressing` holds only what differs from the object's own defaults. */
export function SurfacesPanel(props: {
  /** The object (for a look: the object it dresses). */
  detail: Pick<AssetDetail, "id" | "meta" | "defaults">;
  dressing: Dressing;
  onChange(dressing: Dressing): void;
  selectedSlot: string | null;
  onSelect(slot: string | null): void;
  readOnly?: boolean;
}): JSX.Element {
  const { detail, dressing, onChange, selectedSlot, onSelect } = props;
  const details = useDressingDetails(detail, dressing);
  const [picker, setPicker] = useState<"surface" | "artwork" | null>(null);
  const slots: SlotDoc[] = Array.isArray(detail.meta.slots)
    ? detail.meta.slots
    : [];
  const tintOf = (id: string) => details["material/" + id]?.meta.tint;

  /** What a row says is on its surface, and the colour beside it. */
  const describe = (
    slot: SlotDoc,
    s: SlotSurface,
  ): { text: string; hex: string | null } => {
    if (s.kind === "material") {
      const found = details["material/" + s.id];
      return {
        text: found ? String(found.meta.name) : "…",
        hex: found ? s.color_hex : null,
      };
    }
    if (s.kind === "plain") return { text: "Plain colour", hex: s.color_hex };
    if (s.kind === "image") {
      const picked = dressing.skins[slot.name];
      const name = typeof picked === "object" ? picked.name : "";
      return { text: "Your image: " + name, hex: null };
    }
    const own =
      !(slot.name in dressing.skins) &&
      slot.accepts.length === 1 &&
      slot.accepts[0] === "native";
    if (own) return { text: "Its own texture", hex: null };
    const found = details["skin/" + s.id];
    return { text: found ? String(found.meta.name) : "…", hex: null };
  };

  return (
    <section className="flex flex-col gap-2" data-testid="surfaces-panel">
      <h2 className="tb-h-row m-0">Surfaces</h2>
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {slots.map((slot) => {
          const selected = slot.name === selectedSlot;
          const s = slotSurface(slot, dressing, tintOf);
          const on = describe(slot, s);
          const tint = s.kind === "material" ? tintOf(s.id) : undefined;
          const takesColour =
            s.kind === "plain" ||
            (s.kind === "material" &&
              (tint === "replace" || tint === "multiply"));
          const changed =
            slot.name in dressing.materials ||
            slot.name in dressing.skins ||
            slot.name in dressing.colors_hex;
          return (
            <li key={slot.name}>
              <button
                type="button"
                aria-pressed={selected}
                data-testid="surface-row"
                onClick={() => {
                  setPicker(null);
                  onSelect(selected ? null : slot.name);
                }}
                className={[
                  "flex min-h-[44px] w-full items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left text-sm transition-colors",
                  selected
                    ? "border-brand bg-brand-tint text-brand-dark"
                    : "border-slate-200 bg-white text-slate-800 hover:border-slate-300",
                ].join(" ")}
              >
                <span className="font-semibold">{humanise(slot.name)}</span>
                <span className="flex min-w-0 items-center gap-2 text-slate-600">
                  <span className="min-w-0 break-words text-right">
                    {on.text}
                  </span>
                  {on.hex ? <Swatch hex={on.hex} /> : null}
                </span>
              </button>
              {selected && !props.readOnly ? (
                <div
                  className="mt-2 flex flex-col gap-3 rounded-lg bg-slate-50 p-3"
                  data-testid="surface-controls"
                >
                  <div className="flex flex-wrap gap-2">
                    {slot.accepts.some((c) => CHOOSABLE.includes(c)) ? (
                      <Button
                        type="button"
                        size="sm"
                        className="min-h-[44px] sm:min-h-0"
                        onClick={() => setPicker("surface")}
                      >
                        Choose a surface
                      </Button>
                    ) : null}
                    {slot.region ? (
                      <Button
                        type="button"
                        size="sm"
                        className="min-h-[44px] sm:min-h-0"
                        onClick={() => setPicker("artwork")}
                      >
                        Choose artwork
                      </Button>
                    ) : null}
                    {changed ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="min-h-[44px] sm:min-h-0"
                        onClick={() =>
                          onChange({
                            ...dressing,
                            materials: without(dressing.materials, slot.name),
                            skins: withoutSkin(dressing.skins, slot.name),
                            colors_hex: without(dressing.colors_hex, slot.name),
                          })
                        }
                      >
                        Reset
                      </Button>
                    ) : null}
                  </div>
                  {takesColour ? (
                    <ColourControl
                      value={
                        s.kind === "plain" || s.kind === "material"
                          ? s.color_hex
                          : null
                      }
                      onChange={(hex) =>
                        onChange({
                          ...dressing,
                          colors_hex: {
                            ...dressing.colors_hex,
                            [slot.name]: hex,
                          },
                        })
                      }
                    />
                  ) : null}
                  <SurfacePicker
                    open={picker === "surface"}
                    onClose={() => setPicker(null)}
                    accepts={slot.accepts}
                    onChoose={(item) => {
                      setPicker(null);
                      onChange({
                        ...dressing,
                        materials: {
                          ...dressing.materials,
                          [slot.name]: item.id,
                        },
                        skins: withoutSkin(dressing.skins, slot.name),
                        colors_hex: without(dressing.colors_hex, slot.name),
                      });
                    }}
                  />
                  {slot.region ? (
                    <ArtworkPicker
                      open={picker === "artwork"}
                      onClose={() => setPicker(null)}
                      slot={slot}
                      onChoose={(choice) => {
                        setPicker(null);
                        onChange({
                          ...dressing,
                          skins: { ...dressing.skins, [slot.name]: choice },
                          materials: without(dressing.materials, slot.name),
                          colors_hex: without(dressing.colors_hex, slot.name),
                        });
                      }}
                    />
                  ) : null}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
